// ---------------------------------------------------------------------------
// PB-T.16: Visual "+ Add Section" picker grouped by category
// PB-T.18: "My Sections" uses the mounted saved-section backend
// ---------------------------------------------------------------------------
// Grid layout, collapsible categories, CSS variables only, Lucide icons.
// ---------------------------------------------------------------------------

import { useState, type CSSProperties } from "react";
import { Skeleton } from "@/components/skeleton";
import {
  ChevronDown,
  ChevronRight,
  DollarSign,
  Grid3x3,
  HelpCircle,
  LayoutGrid,
  MousePointerClick,
  Quote,
  Sparkles,
  Trash2,
} from "lucide-react";
import type { SectionTemplate } from "../../data/section-templates";
import {
  SECTION_TEMPLATES,
  TEMPLATE_CATEGORIES,
} from "../../data/section-templates";
import {
  deleteSavedSection,
  useSavedSections,
  type SavedSectionPuckData,
  type SavedSectionRecord,
} from "@/hooks/use-saved-sections";

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  hero: <Sparkles size={16} />,
  features: <Grid3x3 size={16} />,
  testimonials: <Quote size={16} />,
  pricing: <DollarSign size={16} />,
  cta: <MousePointerClick size={16} />,
  faq: <HelpCircle size={16} />,
  other: <LayoutGrid size={16} />,
};

type TabId = "templates" | "my-sections";

export interface SectionInsertTemplate extends SectionTemplate {
  puckData?: SavedSectionPuckData;
  savedSectionId?: string;
}

const TAB_BAR: CSSProperties = {
  display: "flex",
  borderBottom: "1px solid var(--color-border)",
  marginBottom: "8px",
};

const TAB_BASE: CSSProperties = {
  flex: 1,
  padding: "8px 12px",
  fontSize: "0.8125rem",
  fontWeight: 600,
  background: "none",
  border: "none",
  borderBottomWidth: "2px",
  borderBottomStyle: "solid",
  borderBottomColor: "transparent",
  cursor: "pointer",
  color: "var(--color-muted-foreground)",
  transition: "color 0.15s, border-color 0.15s",
};

const TAB_ACTIVE: CSSProperties = {
  ...TAB_BASE,
  color: "var(--color-primary)",
  borderBottomColor: "var(--color-primary)",
};

interface SectionPickerProps {
  onSelect: (template: SectionInsertTemplate) => void;
}

export function SectionPicker({ onSelect }: SectionPickerProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<TabId>("templates");
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const { sections, isLoading, error, refresh } = useSavedSections();

  const toggleCategory = (categoryId: string) => {
    setCollapsed((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const templatesByCategory = TEMPLATE_CATEGORIES.map((cat) => ({
    ...cat,
    templates: SECTION_TEMPLATES.filter((t) => t.category === cat.id),
  }));

  const savedTemplates = sections
    .map(mapSavedSectionToTemplate)
    .filter((template): template is SectionInsertTemplate => template !== null);

  const handleRemoveSavedSection = async (sectionId: string) => {
    setRemovingId(sectionId);
    setRemoveError(null);
    try {
      await deleteSavedSection(sectionId);
      await refresh();
    } catch (error) {
      setRemoveError(error instanceof Error ? error.message : "Failed to delete saved section");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
      <div style={TAB_BAR}>
        <button
          type="button"
          style={activeTab === "templates" ? TAB_ACTIVE : TAB_BASE}
          onClick={() => setActiveTab("templates")}
        >
          Templates
        </button>
        <button
          type="button"
          style={activeTab === "my-sections" ? TAB_ACTIVE : TAB_BASE}
          onClick={() => setActiveTab("my-sections")}
        >
          My Sections
        </button>
      </div>

      {activeTab === "my-sections" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {isLoading ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "8px",
              }}
            >
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    border: "1px solid var(--color-border)",
                    borderRadius: "8px",
                    background: "var(--color-card)",
                    overflow: "hidden",
                    padding: "12px 8px",
                  }}
                >
                  <Skeleton className="h-12 w-full rounded-[4px]" />
                  <Skeleton className="mx-auto mt-3 h-4 w-3/4" />
                  <Skeleton className="mt-3 h-8 w-full rounded-none" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div style={{ ...EMPTY_STATE, border: "1px solid var(--color-border)", borderRadius: "8px", background: "var(--color-card)" }}>
              <p style={{ margin: "0 0 8px", color: "var(--color-foreground)", fontWeight: 600 }}>
                Saved sections could not be loaded.
              </p>
              <p style={{ margin: 0, lineHeight: 1.6 }}>{error}</p>
            </div>
          ) : savedTemplates.length === 0 ? (
            <div style={{ ...EMPTY_STATE, border: "1px solid var(--color-border)", borderRadius: "8px", background: "var(--color-card)" }}>
              <p style={{ margin: "0 0 8px", color: "var(--color-foreground)", fontWeight: 600 }}>
                No saved sections yet.
              </p>
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                Save a section from the page editor toolbar, then reuse it here across the same tenant workspace.
              </p>
            </div>
          ) : (
            <>
              {removeError ? (
                <div
                  role="alert"
                  style={{
                    padding: "10px 12px",
                    border: "1px solid var(--color-danger, #dc2626)",
                    borderRadius: "8px",
                    background: "color-mix(in srgb, var(--color-danger, #dc2626) 8%, var(--color-card))",
                    color: "var(--color-foreground)",
                    fontSize: "0.75rem",
                    lineHeight: 1.5,
                  }}
                >
                  {removeError}
                </div>
              ) : null}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "8px",
                }}
              >
                {savedTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="stagger-item"
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "8px",
                      background: "var(--color-card)",
                      overflow: "hidden",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(template)}
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "6px",
                        padding: "12px 8px",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "48px",
                          borderRadius: "4px",
                          background: template.thumbnail,
                        }}
                      />
                      <span
                        style={{
                          fontSize: "0.75rem",
                          color: "var(--color-foreground)",
                          fontWeight: 500,
                          textAlign: "center",
                          lineHeight: 1.3,
                        }}
                      >
                        {template.name}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleRemoveSavedSection(template.id)}
                      disabled={removingId === template.id}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        padding: "8px",
                        border: "none",
                        borderTop: "1px solid var(--color-border)",
                        background: "var(--color-background)",
                        color: "var(--color-muted-foreground)",
                        cursor: removingId === template.id ? "wait" : "pointer",
                        fontSize: "0.75rem",
                      }}
                    >
                      {removingId === template.id ? (
                        <>
                          <Skeleton className="h-3.5 w-3.5" />
                          <Skeleton className="h-3 w-10" />
                        </>
                      ) : (
                        <>
                          <Trash2 size={14} />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {templatesByCategory.map((category) => {
            const isCollapsed = collapsed[category.id] ?? false;

            return (
              <div
                key={category.id}
                style={{
                  border: "1px solid var(--color-border)",
                  borderRadius: "8px",
                  overflow: "hidden",
                  background: "var(--color-card)",
                }}
              >
                <button
                  type="button"
                  onClick={() => toggleCategory(category.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    padding: "12px 16px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--color-foreground)",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  {CATEGORY_ICONS[category.id]}
                  <span>{category.label}</span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: "0.75rem",
                      color: "var(--color-muted-foreground)",
                      fontWeight: 400,
                    }}
                  >
                    {category.templates.length}
                  </span>
                </button>

                {!isCollapsed && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "8px",
                      padding: "0 12px 12px",
                    }}
                  >
                    {category.templates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => onSelect(template)}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "6px",
                          padding: "12px 8px",
                          border: "1px solid var(--color-border)",
                          borderRadius: "6px",
                          background: "var(--color-background)",
                          cursor: "pointer",
                          transition: "border-color 0.15s, box-shadow 0.15s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "var(--color-primary)";
                          e.currentTarget.style.boxShadow = "0 0 0 1px var(--color-primary)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = "var(--color-border)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: "48px",
                            borderRadius: "4px",
                            background: template.thumbnail,
                          }}
                        />
                        <span
                          style={{
                            fontSize: "0.75rem",
                            color: "var(--color-foreground)",
                            fontWeight: 500,
                            textAlign: "center",
                            lineHeight: 1.3,
                          }}
                        >
                          {template.name}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const EMPTY_STATE: CSSProperties = {
  padding: "32px 16px",
  textAlign: "center",
  color: "var(--color-muted-foreground)",
  fontSize: "0.8125rem",
};

function mapSavedSectionToTemplate(section: SavedSectionRecord): SectionInsertTemplate | null {
  const content = Array.isArray(section.puckData.content) ? section.puckData.content : [];
  if (content.length === 0) {
    return null;
  }

  const normalizedCategory = section.category === "header" ? "hero" : section.category;
  const category = TEMPLATE_CATEGORIES.some((item) => item.id === normalizedCategory)
    ? (normalizedCategory as SectionTemplate["category"])
    : "other";

  return {
    id: section.id,
    name: section.name,
    category,
    savedSectionId: section.id,
    puckData: section.puckData,
    thumbnail:
      section.thumbnail
      ?? "linear-gradient(135deg, var(--color-card) 0%, var(--color-muted) 100%)",
    content: content.map((block) => ({
      type: block.type,
      props: block.props ?? {},
    })),
  };
}
